import { useCallback, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useCart from 'hooks/use-cart';
import useCustomerState from 'hooks/use-customer-state';
import useErnie from 'shared/hooks/use-ernie';
import useUI from 'hooks/use-ui';
import { checkAllLimits } from 'shared/helpers/purchase-limits';
import { cartItemId } from 'utils/helpers/product';
import { consumerDispensaries } from 'shared/graphql/dispensary/queries';
import { ERNIE_TYPES } from 'shared/constants';
import { getLogger } from 'utils/datadog-logger';
import type { Dispensaries } from 'src/gql/graphql';
import { useReorderUnavailableItemsModal } from 'components/modals/reorder-unavailable-items';
import { ConsumerOrder } from 'src/components/embedded-account/account.types';
import { tracker } from 'src/utils/analytics';
import { isGtmV2Enabled } from 'src/utils/analytics/helpers/gtm-v2-flag';
import { useReorderItems, ReorderAvailableItem, ReorderUnavailableItem, UnavailableReason } from '../use-reorder-items';

export const REORDER_FLAG = 'ecomm.one-click-reording.rollout';

const logger = getLogger('one-click-reorder');
const REORDER_ERROR_MESSAGE = 'Something went wrong with your reorder. Please try again.';

export type ReorderSource = 'buy it again carousel' | 'order list' | 'order receipt';

export type ReorderOptions = {
  source?: ReorderSource;
};

type UseReorderReturn = {
  reorder: ((order: ConsumerOrder, options?: ReorderOptions) => Promise<void>) | undefined;
  loadingOrderId: string | null;
};

export function useReorder(): UseReorderReturn {
  const flags = useFlags();
  const isEnabled: boolean = flags[REORDER_FLAG] ?? false;
  const apolloClient = useApolloClient();
  const Cart = useCart();
  const customerState = useCustomerState();
  const showErnie = useErnie();
  const UI = useUI();
  const { fetchReorderItems } = useReorderItems();
  const unavailableItemsModal = useReorderUnavailableItemsModal();
  const [loadingOrderId, setLoadingOrderId] = useState<string | null>(null);
  const reorderingRef = useRef(false);

  const reorder = useCallback(
    async (order: ConsumerOrder, options?: ReorderOptions): Promise<void> => {
      const dispensaryId = order.dispensary?._id;
      const { orderId } = order;
      if (!dispensaryId || !order.orders.length || !orderId) {
        return;
      }

      if (reorderingRef.current) {
        return;
      }
      reorderingRef.current = true;
      setLoadingOrderId(orderId);
      try {
        // Fetch dispensary and product availability in parallel.
        // fetchReorderItems only needs the dispensaryId string, not the full
        // dispensary object, so these two queries are independent.
        const [dispoResult, { availableItems, unavailableItems }] = await Promise.all([
          apolloClient.query({
            query: consumerDispensaries,
            variables: { dispensaryFilter: { cNameOrID: dispensaryId } },
          }),
          fetchReorderItems(order.orders, dispensaryId, UI.isKiosk),
        ]);

        // We still need the full dispensary object for Cart.addItem.
        const fetchedDispensary = dispoResult?.data?.filteredDispensaries?.[0];
        if (!fetchedDispensary) {
          logger.error('dispensary not found', { dispensaryId, orderId });
          showErnie(REORDER_ERROR_MESSAGE, ERNIE_TYPES.ERROR);
          return;
        }

        if (availableItems.length === 0) {
          if (unavailableItems.length > 0) {
            await unavailableItemsModal.open({ unavailableItems });
          }
          return;
        }

        const trackerSource = options?.source ? `reorder > ${options.source}` : 'reorder';

        const limitExceededItems = addItemsToCart(
          availableItems,
          Cart,
          fetchedDispensary,
          trackerSource,
          customerState
        );

        const allUnavailableItems = [...unavailableItems, ...limitExceededItems];
        if (allUnavailableItems.length > 0) {
          await unavailableItemsModal.open({ unavailableItems: allUnavailableItems });
        }

        const itemsWereAdded = limitExceededItems.length < availableItems.length;
        if (itemsWereAdded) {
          UI.openCartDrawer();
        }
      } catch (err) {
        logger.error('reorder failed', { orderId, dispensaryId, error: String(err) });
        showErnie(REORDER_ERROR_MESSAGE, ERNIE_TYPES.ERROR);
      } finally {
        reorderingRef.current = false;
        setLoadingOrderId(null);
      }
    },
    [Cart, UI, apolloClient, customerState, fetchReorderItems, showErnie, unavailableItemsModal]
  );

  if (!isEnabled) {
    return { reorder: undefined, loadingOrderId: null };
  }

  return { reorder, loadingOrderId };
}

function addItemsToCart(
  items: ReorderAvailableItem[],
  cart: ReturnType<typeof useCart>,
  dispensary: Dispensaries,
  trackerSource: string,
  customerState: string | undefined
): ReorderUnavailableItem[] {
  const addedCartItems: Array<{
    product: ReorderAvailableItem['product'];
    option: string;
    quantity: number;
    additionalOption?: string;
    trackerSource: string;
  }> = [];
  const limitExceededItems: ReorderUnavailableItem[] = [];

  items.forEach((item) => {
    const cartItem = {
      product: item.product,
      option: item.option,
      quantity: item.quantity,
      additionalOption: item.additionalOption,
      trackerSource,
    };
    const id = cartItemId(cartItem);

    // cart.items is read fresh each iteration because cart.addItem (below)
    // synchronously updates the MobX store, so cumulative limits are enforced.
    const limitsCheck = checkAllLimits({
      newItemId: id,
      itemBeingAdded: cartItem,
      existingItems: cart.items,
      medical: cart.isMedical,
      dispensary,
      customerState,
    });

    if (!limitsCheck.withinLimits) {
      limitExceededItems.push({
        name: item.product.Name ?? item.product.name ?? 'Unknown Item',
        image: item.product.Image ?? undefined,
        option: item.option !== 'N/A' ? item.option : undefined,
        reason: UnavailableReason.overLimit,
        requestedQuantity: item.quantity,
        limitMessage: 'message' in limitsCheck ? limitsCheck.message : undefined,
      });
      return;
    }

    cart.addItem(dispensary, cartItem, id);
    addedCartItems.push(cartItem);
  });

  // Fire analytics after all items are in the cart.
  // analyticsProductAddedToCart calls tracker.setContext internally
  // with the cart store's dispensary, so running it after addItem
  // ensures the context reflects the correct dispensary and full cart.
  addedCartItems.forEach((cartItem) => {
    cart.analyticsProductAddedToCart(cartItem);
    if (isGtmV2Enabled()) {
      cart.addPendingAnalyticsEvent({ type: 'add', payload: cartItem });
    } else {
      tracker.addedProductToCart(cartItem);
    }
  });

  return limitExceededItems;
}
